/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from "react";
import parse from "html-react-parser";
import moment from "moment";
import { UncontrolledTooltip } from "reactstrap";
import { DownloadCloud } from "react-feather";
import { MessagesSquare } from "lucide-react";
import { toast } from "react-toastify";
import SweetAlert from "sweetalert2";
import { humanDateFormat, humanTimeFormat } from "../../../../config/constants";
import { useAuth } from "../../../context/AuthContext";
import { handleErrors } from "../../../services/handleErrors";
import { postMessageTicketing, postNoteTicketing, patchMessageNewSolucion } from "../../../services/messageTicketingService";
import { downloadFile } from "../../../services/fileServices";
import modulos from "../../../mocks/modulos";
import ConfirmarMensajeModal from "../ConfirmMsgModal";
import ReopenTicketModal from "../ReopenTicketModal";
import useMutter from "../../../hooks/useMutter";
import { patchTicketing } from "../../../services/ticketingServices";
import ReplyMessage from "./ReplyMessage";
import { ChatMessage, CustomTextArea, CustomToolBarGroup } from "./styles";
import { ButtonPrimary, IconButton } from "../../../ui/buttons";
import MessageTypeButtons from "./MessageTypeButtons";

const replyTypeChat = {
  NOTE: "nota",
  COMMENT: "comment",
};

const getPlaceholder = (replyType) => {
  if (replyType === "comment") {
    return "Comentario";
  }
  return "Nota";
};

const Chat = ({
  ticketId,
  ticket,
  messages,
  setMessages,
  conversationSubject,
  conversation,
  isConversationRoot,
  addNewMessage,
  lastMessageId,
  changeReplyMessage,
  handleReload,
  isAudit,
  isReplyMessage,
  setIsReplyMessage,
  handleFilesChange,
}) => {
  const { currentUser, refreshToken, haveSucursalCasaCentralAsignada } = useAuth();
  const [inputText, setInputText] = useState("");
  const [replyType, setReplyType] = useState(replyTypeChat.COMMENT);
  const [confirmMsg, setconfirmMsg] = useState(false);
  const currentUserId = currentUser && currentUser.user.Pi.itemId;
  const isAdmin = currentUser && currentUser.haveAdminAccess(modulos.TICKETING);
  const isOwner = ticket.personalInternoAsignado === currentUserId;
  const isCreator = ticket.creadoPara === currentUserId;
  const isTeam = ticket && ticket.SucursaldepartamentoAsignado && haveSucursalCasaCentralAsignada(ticket.SucursaldepartamentoAsignado.itemId);
  const colaboradores = (conversation && !conversation.isRoot && conversation.colaboradores) || [];
  const isColaborador = colaboradores.map((colaborador) => colaborador.itemId).includes(currentUserId);
  const isWatcher = ticket && ticket.Watchers && ticket.Watchers.map((watcher) => watcher.itemId).includes(currentUserId);
  const canResponder = ticket.editable && (isOwner || isAdmin || isTeam || isColaborador || isCreator || isWatcher) && !isAudit;
  const canResolver = ticket.editable && (isOwner || isAdmin || isTeam || isWatcher) && !isAudit;

  const { status } = ticket;
  const [saveTicketCall] = useMutter(patchTicketing);

  const pi = currentUser.user.Pi.itemId;
  const isReopenable = !!(status === 5 && pi);

  const modalConfirm = !!(confirmMsg && !isReopenable);
  const modalReopenable = !!(confirmMsg && isReopenable);

  const handleTicketSave = async (newData, options) => {
    const [result] = await saveTicketCall([ticketId, newData], options);
    return result;
  };

  const handleChangeInput = (event) => {
    const message = event.target.value;

    // Expresión regular para buscar un hipervínculo dentro de <>
    const regex = /<([^>]+)>/g;
    const matches = message.match(regex);

    if (matches) {
      // Si se encontraron coincidencias, reemplaza el mensaje original por una versión sin <>
      const formattedMessage = message.replace(/[<>]/g, "");
      setInputText(formattedMessage);
    } else {
      // No se encontraron hipervínculos, establece el mensaje original
      setInputText(message);
    }
  };

  // Scroll chat to bottom
  const scrollChatToBottom = () => {
    const scrollChat = document.getElementById("chatBox");
    if (scrollChat) {
      scrollChat.scrollTop = scrollChat.scrollHeight;
    }
  };
  const sendNewMessage = async (newMessage) => {
    let resp;
    try {
      if (newMessage.to) {
        resp = await postMessageTicketing(ticketId, newMessage);
      } else {
        resp = await postNoteTicketing(ticketId, newMessage);
      }
      const { result } = resp;
      if (result.lastMessageId) {
        addNewMessage(result.messages[0]);
      }
      setInputText("");
      scrollChatToBottom();
    } catch (error) {
      handleErrors(error);
    }
  };

  const getEmailInvolucrados = () => {
    if (messages.length > 0) {
      const { from } = messages[0];
      let tos = [];
      if (messages[0].to && messages[0].to.length > 0) {
        const toList = messages[0].to;
        tos = toList.map((item) => {
          return item.email;
        });
      }
      return [].concat(tos).concat(from);
    }
    return [];
  };

  const isEmail = (email) => /\S+@\S+/.test(email);

  const getEmailCreador = () => {
    const posiblesEmails = [ticket.CreadoPara.titulo, ticket.CreadoPara.title, ticket.CreadoPara.email2];
    const email = posiblesEmails.find((d) => isEmail(d));
    return email;
  };

  const getEmailColaborador = (colaborador) => {
    const posiblesEmails = [colaborador.titulo, colaborador.email2];
    const email = posiblesEmails.find((d) => isEmail(d));
    return email;
  };

  const getColorMessage = (message) => {
    if (message.isSolucion) {
      return { back: "#F7F7F7", border: "#F7F7F7" };
    }
    if (message.isSolicitud) {
      return { back: "#F7F7F7", border: "#F7F7F7" };
    }
    if (message.type === "note") {
      return { back: "rgba(250, 182, 0, 0.2)", border: "rgba(250, 182, 0, 0.2)" };
    }
    if (message.type === "comment") {
      return { back: "#F7F7F7", border: "#F7F7F7" };
    }
    return { back: "#F7F7F7", border: "#F7F7F7" };
  };

  const handleSubmitMessage = () => {
    if (lastMessageId) {
      const toList = getEmailInvolucrados();
      const payload = {
        subject: conversationSubject,
        body: inputText,
        to: toList,
        replyMessageId: lastMessageId,
        isSolucion: false,
        type: replyType,
      };
      if (replyType === replyTypeChat.COMMENT || replyType === replyTypeChat.MESSAGE) {
        setconfirmMsg(!confirmMsg);
        sendNewMessage(payload);
      } else {
        payload.visibility = "private";
        delete payload.to;
        sendNewMessage(payload);
      }
    }
  };

  const handleReopenClick = async () => {
    handleSubmitMessage();
    await handleTicketSave({ status: 2 });
    handleReload();
  };

  const handleDownloadFile = async (file) => {
    try {
      const entity = "ticketing";
      const entityId = ticketId;
      const fileId = file.id;
      await downloadFile({ entity, entityId, fileId });
      toast.success(`Archivo ${file.originalname} descargado exitosamente`);
    } catch (err) {
      handleErrors(err, toast.error, refreshToken);
    }
  };

  const updateMessagesSolucion = async (newSolucionMessage) => {
    let resp;
    try {
      const { conversationId, id: messageId } = newSolucionMessage;
      if (newSolucionMessage) {
        const statusSolution = !newSolucionMessage.isSolucion;
        resp = await patchMessageNewSolucion(ticketId, conversationId, messageId, statusSolution);
      }
      const { result } = resp;
      const updatedMessages = messages.map((m) => {
        if (m.id === result.id) {
          return { ...m, isSolucion: result.isSolucion };
        }
        return { ...m, isSolucion: false };
      });
      setMessages(updatedMessages);
      toast.success("Se actualizó la solución exitosamente");
    } catch (error) {
      handleErrors(error);
    }
  };

  const handleChangeSolucion = async (msg) => {
    const { isConfirmed } = await SweetAlert.fire({
      title: "Resolver",
      text: `${msg.isSolucion ? "¿Seguro que desea quitar este comentario como Solución?" : "¿Seguro que desea marcar este comentario como Solución?"}`,
      // icon: "warning",
      confirmButtonColor: "#EE3124",
      cancelButtonColor: "#ccc",
      showCancelButton: true,
      showConfirmButton: true,
    });

    if (isConfirmed) {
      updateMessagesSolucion(msg);
    }
  };

  const getNameMessage = (message) => {
    if (message.creadoPorId === ticket.creadoPor) {
      return ticket.CreadoPor.nombreApellido || `${ticket.CreadoPor.nombre} ${ticket.CreadoPor.apellido}`;
    }
    if (colaboradores.find((i) => i.itemId === message.creadoPorId)) {
      const user = colaboradores.find((i) => i.itemId === message.creadoPorId);
      return user.nombreApellido;
    }
    return message.from.substring(0, message.from.lastIndexOf("@"));
  };

  useEffect(() => {
    scrollChatToBottom();
  }, [messages]);

  return (
    <>
      {modalReopenable ? (
        <ReopenTicketModal toggle={() => setconfirmMsg(!confirmMsg)} handleReopenClick={handleReopenClick} handleSubmitMessage={handleSubmitMessage} />
      ) : null}
      {modalConfirm ? <ConfirmarMensajeModal toggle={() => setconfirmMsg(!confirmMsg)} handleSubmitMessage={handleSubmitMessage} /> : null}
      <div className="container-fluid" style={{ height: "100%", padding: "10px" }}>
        <div className="call-chat-body h-100">
          <div className="chat-box" style={{ height: "100%" }}>
            <div className="col p-0 chat-right-aside" style={{ maxWidth: "100%", height: "100%" }}>
              <div className="chat h-100">
                <div className="d-flex justify-content-between align-items-center chat-header clearfix px-0">
                  <div className="about w-50 d-flex" style={{ gap: "10px", fontFamily: "Nunito" }}>
                    <MessagesSquare color="var(--secondary)" />
                    <div className="w-100">
                      <div style={{ fontWeight: "bold" }}> {conversationSubject} </div>
                      <div className="status digits">
                        {colaboradores.length === 0 ? getEmailCreador() : null}
                        {messages && colaboradores.length !== 0
                          ? colaboradores.map((c) => {
                              return <div>{getEmailColaborador(c)}</div>;
                            })
                          : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div id="chatBox" className="chat-history chat-msg-box custom-scrollbar px-0 h-100">
                  <ul style={{ marginBottom: "20em" }}>
                    {messages && currentUser && messages.length > 0 ? (
                      messages
                        .sort((a, b) => (a.id > b.id ? 1 : -1))
                        .map((item) => {
                          return (
                            <li key={item.id} className="clearfix">
                              <div
                                className="d-flex flex-column w-100"
                                style={{
                                  marginBottom: "20px",
                                  alignItems: item.creadoPorPiId !== currentUserId ? "end" : "start",
                                }}
                              >
                                <div
                                  className="message my-message w-75"
                                  style={{
                                    backgroundColor: getColorMessage(item).back,
                                    borderColor: getColorMessage(item).border,
                                    paddingTop: item.isSolicitud || item.isSolucion ? "45px" : "15px",
                                  }}
                                >
                                  {(item.isSolicitud || item.isSolucion) && (
                                    <div
                                      className="d-block"
                                      style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        borderRadius: "0 10px 0 0",
                                        border: item.isSolicitud ? "#FAC2BE" : "1px solid #BBEEDF",
                                        backgroundColor: item.isSolicitud ? "#FAC2BE" : "#BBEEDF",
                                        width: "100%",
                                        padding: "5px",
                                        fontFamily: "Nunito",
                                        height: "40px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {item.isSolicitud ? "Solicitud" : "Solución"}
                                    </div>
                                  )}
                                  <>{item.body ? parse(item.body) : ""}</>
                                  {item.Files.length > 0 && (
                                    <div>
                                      <hr />
                                      <strong>Archivo</strong>
                                      <ul className="mb-0">
                                        {item.Files.map((file) => {
                                          return (
                                            <li className="d-flex justify-content-between" key={file.id} style={{ fontFamily: "Nunito" }}>
                                              <span>{file.originalname}</span>
                                              <span>
                                                <UncontrolledTooltip placement="top" target={`downloadFile${file.id}`}>
                                                  Descargar
                                                </UncontrolledTooltip>
                                                <IconButton outline onClick={() => handleDownloadFile(file)} id={`downloadFile${file.id}`}>
                                                  <DownloadCloud size="20" />
                                                </IconButton>
                                              </span>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                                <div className="d-flex align-items-center mt-1">
                                  <span className="mr-2" style={{ fontFamily: "Nunito", fontSize: "12px" }}>
                                    {getNameMessage(item)} {" - ".concat(moment(item.createdAt).format(humanDateFormat))} {moment(item.createdAt).format(humanTimeFormat)}
                                  </span>
                                  {isConversationRoot && (
                                    <span>
                                      {item.type !== "note" && !item.isSolicitud && canResolver && (
                                        <label className="d-block mb-0" htmlFor="isSolucion">
                                          <input
                                            className="checkbox_red"
                                            id="isSolucion"
                                            name="isSolucion"
                                            title="Marcar como solución"
                                            type="checkbox"
                                            checked={item.isSolucion}
                                            onChange={() => handleChangeSolucion(item)}
                                          />
                                        </label>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </li>
                          );
                        })
                    ) : (
                      <div> Cargando... </div>
                    )}
                  </ul>
                </div>
                {canResponder && (
                  <ChatMessage>
                    {replyType !== replyTypeChat.MESSAGE && !isReplyMessage && (
                      <>
                        <div className="d-flex justify-content-between align-items-center w-100">
                          <CustomToolBarGroup padding="10px 10px 0">
                            <MessageTypeButtons
                              changeReplyMessage={changeReplyMessage}
                              isReplyMessage={isReplyMessage}
                              setReplyType={setReplyType}
                              replyTypeChat={replyTypeChat}
                              replyType={replyType}
                            />
                          </CustomToolBarGroup>
                        </div>
                        <div className="d-flex flex-column px-2 pb-2">
                          {replyType === replyTypeChat.COMMENT ? (
                            <span className="text-muted mx-2" style={{ fontFamily: "Nunito", alignSelf: "flex-end" }}>
                              Para mensajes internos, utilizar Nota
                            </span>
                          ) : (
                            <br />
                          )}
                          <div className="d-flex flex-column w-100" style={{ gap: "10px" }}>
                            <CustomTextArea placeholder={getPlaceholder(replyType)} onChange={handleChangeInput} value={inputText} />
                            <div className="d-flex justify-content-end w-100">
                              {replyType === replyTypeChat.NOTE ? (
                                <ButtonPrimary type="button" onClick={handleSubmitMessage} disabled={!inputText}>
                                  Enviar
                                </ButtonPrimary>
                              ) : (
                                <ButtonPrimary type="button" onClick={() => setconfirmMsg(!confirmMsg)} disabled={!inputText}>
                                  Enviar
                                </ButtonPrimary>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    {isReplyMessage && (
                      <ReplyMessage
                        ticketId={ticketId}
                        ticket={ticket}
                        messages={messages}
                        addMessage={addNewMessage}
                        isConversationRoot={isConversationRoot}
                        setIsReplyMessage={setIsReplyMessage}
                        lastMessageId={lastMessageId}
                        handleFilesChange={handleFilesChange}
                        changeReplyMessage={changeReplyMessage}
                        isReplyMessage={isReplyMessage}
                        setReplyType={setReplyType}
                        replyTypeChat={replyTypeChat}
                      />
                    )}
                  </ChatMessage>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chat;
